import React, { useState, useCallback, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Download, Loader2, CheckCircle, FileDown } from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { useUpstreamFile } from '../useUpstreamFile';
import { sheetsService, SchemaColumn } from '../../../services/sheetsService';
import { s3Config } from '../../../aws-config';

type ExportFormat = 'csv' | 'json';

export const ExportNode: React.FC<{ id: string; data: { label: string; color: string; isTemplate?: boolean } }> = ({ id, data }) => {
  const selectedFile = useUpstreamFile(id);
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [status, setStatus] = useState<'idle' | 'exporting' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    setStatus('idle');
    setErrorMsg('');
  }, [selectedFile]);

  const handleExport = useCallback(async () => {
    if (!selectedFile) return;
    setStatus('exporting');
    setErrorMsg('');

    try {
      const bucket = s3Config.primaryDataBucket;
      const s3Key = selectedFile.s3Key || `users/placeholder/${selectedFile.fileName}`;

      // First get schema to find a text column for the match-all query
      const schemaRes = await sheetsService.getSchema(bucket, s3Key, { isMultipart: selectedFile.isMultipart });
      if (!schemaRes.success || !schemaRes.schema) {
        throw new Error('Failed to fetch schema');
      }

      const textCol = schemaRes.schema.find(
        (c: SchemaColumn) => c.data_type === 'BYTE_ARRAY' || c.data_type === 'Utf8' || c.data_type === 'LargeUtf8' || c.data_type.includes('Utf8')
      ) || schemaRes.schema[0];

      // Fetch all rows (up to 50,000)
      const dataRes = await sheetsService.search(
        bucket,
        s3Key,
        { column: textCol.name, operator: 'contains', value: '' },
        { limit: 50000, offset: 0, isMultipart: selectedFile.isMultipart }
      );

      if (!dataRes.success || !dataRes.data || dataRes.data.length === 0) {
        throw new Error('No data to export');
      }

      const rows = dataRes.data;
      const columns = schemaRes.schema.map((c: SchemaColumn) => c.name);
      let content: string;
      let mimeType: string;
      let extension: string;

      if (format === 'csv') {
        const header = columns.map((c: string) => `"${c.replace(/"/g, '""')}"`).join(',');
        const body = rows.map((row: Record<string, any>) =>
          columns.map((c: string) => {
            const val = row[c];
            if (val == null) return '';
            const str = String(val);
            return `"${str.replace(/"/g, '""')}"`;
          }).join(',')
        ).join('\n');
        content = header + '\n' + body;
        mimeType = 'text/csv';
        extension = 'csv';
      } else {
        content = JSON.stringify(rows, null, 2);
        mimeType = 'application/json';
        extension = 'json';
      }

      // Trigger download
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = selectedFile.fileName.replace(/\.[^.]+$/, '');
      a.download = `${baseName}.${extension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus('done');
    } catch (e: any) {
      setStatus('error');
      setErrorMsg(e.message || 'Export failed');
    }
  }, [selectedFile, format]);

  const reset = () => {
    setStatus('idle');
    setErrorMsg('');
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[320px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <FileDown className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {!selectedFile ? (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Connect a data source
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1">
            <span className="truncate">{selectedFile.fileName}</span>
          </div>
        )}

        {selectedFile && status === 'idle' && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Format:</span>
              <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
                <SelectTrigger className="h-7 text-[11px] w-[100px] nodrag">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv" className="text-xs">CSV</SelectItem>
                  <SelectItem value="json" className="text-xs">JSON</SelectItem>
                </SelectContent>
              </Select>
              {selectedFile.totalRows && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-auto">
                  {selectedFile.totalRows.toLocaleString()} rows
                </Badge>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="w-full text-xs h-7"
              onClick={handleExport}
            >
              <Download className="h-3 w-3 mr-1" />
              Download as {format.toUpperCase()}
            </Button>
          </>
        )}

        {status === 'exporting' && (
          <div className="flex items-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-xs">Exporting {selectedFile?.fileName}...</span>
          </div>
        )}

        {status === 'done' && (
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
            <span className="text-xs truncate flex-1">Downloaded as {format.toUpperCase()}</span>
            <Button variant="ghost" size="sm" className="h-6 text-[11px]" onClick={reset}>
              Again
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-1">
            <p className="text-xs text-destructive">{errorMsg}</p>
            <Button variant="outline" size="sm" className="w-full text-xs h-7" onClick={reset}>
              Try again
            </Button>
          </div>
        )}
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
    </div>
  );
};
